import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getMaxShares, OFFERING_CONSTANTS, stub } from '@arrived/common';
import { OfferingId, OfferingStatus } from '@arrived/models';
import { useOfferingQuery, useSharesAvailableQuery } from '@arrived/queries';

export interface ShareSelectionContextType {
  decrementShares: () => void;
  incrementShares: () => void;
  maxShares: number;
  minShares: number;
  setSharesToBuy: Dispatch<SetStateAction<number>>;
  sharesToBuy: number;
  subtotal: number;
}

export const ShareSelectionContext = createContext<ShareSelectionContextType>({
  decrementShares: stub,
  incrementShares: stub,
  maxShares: 0,
  minShares: 0,
  setSharesToBuy: stub,
  sharesToBuy: 0,
  subtotal: 0,
});

export interface ShareSelectionContextProviderProps {
  offeringId: OfferingId;

  /**
   * Redirect to the offering page if the offering is not in a transactable state
   */
  nonTransactableRedirect: () => void;
}

export const ShareSelectionContextProvider = ({
  children,
  offeringId,
  nonTransactableRedirect,
}: PropsWithChildren<ShareSelectionContextProviderProps>) => {
  const offeringState = useOfferingQuery(offeringId);
  const sharesAvailableState = useSharesAvailableQuery(offeringId);

  const [sharesToBuy, setSharesToBuy] = useState(0);

  useEffect(() => {
    if (
      offeringState.isSuccess &&
      ![OfferingStatus.TRANSACT_ALL, OfferingStatus.TRANSACT_EARLY_ACCESS, OfferingStatus.RESERVATIONS].includes(
        offeringState.data?.status
      )
    ) {
      nonTransactableRedirect();
    }
  }, [offeringState.isSuccess, offeringState.data?.status]);

  const minShares = useMemo(() => {
    return (
      (offeringState.data?.minTransactionAmount ?? OFFERING_CONSTANTS.MIN_SHARES_PRICE) /
      (offeringState.data?.sharePrice ?? OFFERING_CONSTANTS.DEFAULT_SHARE_PRICE)
    );
  }, [offeringState.data?.minTransactionAmount, offeringState.data?.sharePrice]);

  const maxShares = useMemo(() => {
    return offeringState.isSuccess && sharesAvailableState.isSuccess
      ? getMaxShares({ offering: offeringState.data, sharesAvailable: sharesAvailableState.data })
      : 0;
  }, [offeringState.data, sharesAvailableState.data]);

  const subtotal = useMemo(() => {
    return offeringState.isSuccess
      ? offeringState.data.sharePrice * sharesToBuy
      : OFFERING_CONSTANTS.DEFAULT_SHARE_PRICE * sharesToBuy;
  }, [offeringState.data, sharesToBuy]);

  const decrementShares = useCallback(
    () => setSharesToBuy((sharesToBuy) => Math.max(0, sharesToBuy - OFFERING_CONSTANTS.SHARES_TO_BUY_STEP_VALUE)),
    []
  );
  const incrementShares = useCallback(
    () => setSharesToBuy((sharesToBuy) => sharesToBuy + OFFERING_CONSTANTS.SHARES_TO_BUY_STEP_VALUE),
    []
  );

  useEffect(() => {
    if (
      OFFERING_CONSTANTS.DEFAULT_SELECTED_SHARES < minShares ||
      OFFERING_CONSTANTS.DEFAULT_SELECTED_SHARES > maxShares
    ) {
      const shareAtmOptions = [...OFFERING_CONSTANTS.SHARE_SELECTOR_VALUES, maxShares];
      const lowestSelectableShareIndex = shareAtmOptions.findIndex((value) => value <= maxShares && value >= minShares);
      setSharesToBuy(shareAtmOptions[lowestSelectableShareIndex]);
    } else {
      setSharesToBuy(OFFERING_CONSTANTS.DEFAULT_SELECTED_SHARES);
    }
  }, [maxShares, minShares, offeringId]);

  const value = useMemo(
    () => ({
      decrementShares,
      incrementShares,
      maxShares,
      minShares,
      setSharesToBuy,
      sharesToBuy,
      subtotal,
    }),
    [maxShares, minShares, sharesToBuy, subtotal]
  );

  return <ShareSelectionContext.Provider value={value}>{children}</ShareSelectionContext.Provider>;
};
